import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { applyPageMeta } from './seoMeta';
import {
  getServicePillar,
  getServicePillarMeta,
  SOUTH_WEST_HUB_SLUGS,
  SERVICE_PILLAR_SLUGS
} from './servicePillarContent';

const SCHEMA_PREFIX = 'webcodecraft-service-pillar-';

function ServicePillarPage() {
  const { pillarSlug } = useParams();
  const pillar = getServicePillar(pillarSlug);
  const meta = getServicePillarMeta(pillarSlug);
  const schemaId = pillar ? `${SCHEMA_PREFIX}${pillarSlug}` : '';

  const jsonLd = useMemo(() => {
    if (!pillar || !meta) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${meta.canonical}#webpage`,
          url: meta.canonical,
          name: meta.title,
          description: meta.description,
          isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
          inLanguage: 'en-GB'
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
            { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_ORIGIN}/services` },
            { '@type': 'ListItem', position: 3, name: pillar.h1, item: meta.canonical }
          ]
        },
        {
          '@type': 'Service',
          name: pillar.h1,
          provider: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN },
          areaServed: { '@type': 'Country', name: 'United Kingdom' },
          description: meta.description
        }
      ]
    });
  }, [pillar, meta]);

  useEffect(() => {
    if (!meta || !pillar) return undefined;
    const restore = applyPageMeta(meta);
    const existing = document.getElementById(schemaId);
    if (!existing && jsonLd) {
      const el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }
    return () => {
      restore();
      const script = document.getElementById(schemaId);
      if (script) script.remove();
    };
  }, [meta, pillar, jsonLd, schemaId]);

  if (!pillarSlug || !SERVICE_PILLAR_SLUGS.includes(String(pillarSlug).toLowerCase()) || !pillar || !meta) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/services" className="hover:underline">
                Services
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">{pillar.h1}</span>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">{pillar.h1}</h1>
            <p className="text-lg font-medium text-indigo-800 mb-6 leading-snug">{pillar.lede}</p>

            <div className="space-y-5 text-gray-700 leading-relaxed mb-10">
              {pillar.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <section className="mb-10 p-6 rounded-2xl bg-white border border-gray-200 shadow-sm" aria-labelledby="pillar-case-heading">
              <h2 id="pillar-case-heading" className="text-xl font-bold text-gray-900 mb-3">
                {pillar.caseTitle}
              </h2>
              <p className="text-gray-700 leading-relaxed">{pillar.caseBody}</p>
            </section>

            <section
              className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white"
              aria-labelledby="pillar-cta-heading"
            >
              <h2 id="pillar-cta-heading" className="text-xl font-bold mb-2">
                {pillar.ctaTitle}
              </h2>
              <p className="mb-4 opacity-95 leading-relaxed">{pillar.ctaBody}</p>
              <a
                href={internalUrl('contact')}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Start a project
              </a>
            </section>

            <section className="mb-10" aria-labelledby="serving-sw-heading">
              <h2 id="serving-sw-heading" className="text-xl font-bold text-gray-900 mb-4">
                Serving the South West
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Explore how we apply this work in major regional hubs—same monthly-first delivery model, tuned to local search and sector norms.
              </p>
              <ul className="flex flex-wrap gap-2">
                {SOUTH_WEST_HUB_SLUGS.map((hub) => (
                  <li key={hub.slug}>
                    <Link
                      to={`/locations/${hub.slug}`}
                      className="inline-block px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors"
                    >
                      {hub.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <p className="text-sm text-gray-500">
              <Link to="/locations" className="text-indigo-600 hover:underline">
                All areas we serve
              </Link>
              {' · '}
              <Link to="/services" className="text-indigo-600 hover:underline">
                Main services overview
              </Link>
            </p>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default ServicePillarPage;
